// src/pages/Commission.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, Users, Star, ExternalLink, CheckCircle, Instagram, Twitter, X, Send } from 'lucide-react';
import SEO from '../components/SEO';

// Shared PageStyles component for consistent styling
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Azonix&display=swap');
    
    :root {
      --az: #a78bfa;
      --jp: #fbbf24;
      --wt: #06b6d4;
      --neu: #8b5cf6;
      --bg: #08080c;
      --glass: rgba(255, 255, 255, 0.03);
      --gb: rgba(167, 139, 250, 0.15);
      --card-bg: linear-gradient(135deg, rgba(20, 20, 30, 0.9), rgba(10, 10, 15, 0.95));
      --accent-gradient: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
    }
    
    * {
      scrollbar-width: thin;
      scrollbar-color: var(--az) var(--bg);
    }
    
    *::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    *::-webkit-scrollbar-track {
      background: var(--bg);
    }
    
    *::-webkit-scrollbar-thumb {
      background: var(--az);
      border-radius: 4px;
    }
    
    .font-azonix {
      font-family: 'Azonix', sans-serif;
    }
    
    .glass-card {
      background: var(--glass);
      backdrop-filter: blur(20px);
      border: 1px solid var(--gb);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }
    
    .glow-border {
      position: relative;
    }
    
    .glow-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.5), rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.5));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
    
    .gradient-text {
      background: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .gradient-glow {
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
    }
  `}</style>
);

export default function Commission() {
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', type: 'character', description: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // NartStudio Comics Universe (NCU) Partnership Info
  const nartuStudio = {
    name: 'NartStudio Comics Universe (NCU)',
    description: 'We are proud to partner with NartStudio Comics Universe, a pioneering creative studio specializing in comic book art, character design, and visual storytelling. This partnership brings you exclusive commissioned artwork from talented artists connected to the NCU creative network.',
    website: 'https://nartstudio.com',
    social: { instagram: '@nartstudio', twitter: '@nartstudioncu' }
  };

  const commissionTypes = [
    {
      id: 'character',
      title: 'Character Design',
      description: 'Original character designs for your OC, fan characters, or RPG avatars.',
      priceRange: '$50 - $500+',
      timeframe: '1-3 weeks',
      icon: Users,
      examples: ['Full body character', 'Chibi style', 'Refsheet', 'Pose variants']
    },
    {
      id: 'illustration',
      title: 'Illustrations',
      description: 'Custom artwork featuring your favorite characters in various scenes.',
      priceRange: '$30 - $400+',
      timeframe: '3-7 days',
      icon: Palette,
      examples: ['Scene illustration', 'Fan art', 'Couple/Group', 'Commercial use']
    },
    {
      id: 'comic',
      title: 'Comic Pages',
      description: 'Full comic pages and panels in various styles.',
      priceRange: '$100 - $1000+',
      timeframe: '2-4 weeks',
      icon: Star,
      examples: ['Single panel', 'Comic strip', 'Full page', 'Comic series']
    },
    {
      id: 'commercial',
      title: 'Commercial Work',
      description: 'Business logos, merchandise designs, and brand artwork.',
      priceRange: '$200 - $2000+',
      timeframe: '1-4 weeks',
      icon: CheckCircle,
      examples: ['Logo design', 'Merchandise art', 'Brand mascot', 'Packaging']
    }
  ];

  const portfolio = [
    { id: 1, title: 'Cyberpunk Character', type: 'Character Design', image: '/assets/team1.jpg', artist: 'NCU Artist' },
    { id: 2, title: 'Fantasy Illustration', type: 'Illustration', image: '/assets/team2.jpg', artist: 'NCU Artist' },
    { id: 3, title: 'Comic Action Scene', type: 'Comic', image: '/assets/team3.jpg', artist: 'NCU Artist' },
    { id: 4, title: 'Brand Mascot', type: 'Commercial', image: '/assets/batman-rumour.jpg', artist: 'NCU Artist' },
    { id: 5, title: 'Anime Style Portrait', type: 'Character', image: '/assets/our-vision1.jpg', artist: 'NCU Artist' },
    { id: 6, title: 'Manga Chapter Cover', type: 'Comic', image: '/assets/our-vision2.jpg', artist: 'NCU Artist' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setContactForm({ name: '', email: '', type: 'character', description: '' });
    }, 3000);
  };

  return (
    <>
      <PageStyles />
      <SEO
        title="Art Commissions - ANIMAC x NartStudio"
        description="Commission custom artwork through ANIMAC's partnership with NartStudio Comics Universe. Character designs, illustrations, comics, and commercial work."
        url="/commission"
        image="/assets/animac-preview-logo.svg"
        type="website"
      />

      <motion.div 
        className="min-h-screen relative overflow-hidden"
        style={{ background: '#08080c' }}
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 0.6 }}
      >
        {/* Background effects */}
        <div className="absolute inset-0 gradient-glow"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 70%)
          `
        }}></div>

        {/* Hero Section with Partnership Banner */}
        <section className="relative py-16 sm:py-20">
          <div className="absolute inset-0 overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1920&q=80" 
              alt="Commissions" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gray-900/80" />
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#a78bfa]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#8b5cf6]/10 rounded-full blur-3xl" />
          </div>
          
          <div className="relative z-10 container mx-auto px-4 text-center">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 glass-card rounded-full mb-4 glow-border"
            >
              <span className="text-[#a78bfa] text-sm font-medium">✨ Official Partnership</span>
            </motion.div>
            
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl font-azonix font-bold mb-4"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Art <span className="gradient-text">Commissions</span>
            </motion.h1>
            <motion.p
              className="text-gray-400 text-lg max-w-2xl mx-auto mb-6"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Custom artwork powered by ANIMAC's partnership with NartStudio Comics Universe
            </motion.p>
          </div>
        </section>

        {/* NartStudio Partnership Section */}
        <section className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-2xl p-6 sm:p-8 glow-border"
          >
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                    background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%)'
                  }}>
                    <Users className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-azonix font-bold text-white">{nartuStudio.name}</h3>
                    <p className="text-[#a78bfa] text-sm">Official Creative Partner</p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {nartuStudio.description}
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href={nartuStudio.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 glass-card text-[#a78bfa] rounded-lg text-sm font-medium hover:bg-[rgba(167,139,250,0.2)] transition-colors"
                  >
                    Visit NartStudio <ExternalLink size={16} />
                  </a>
                  <span className="flex items-center gap-1 text-pink-400 text-sm">
                    <Instagram size={16} /> {nartuStudio.social.instagram}
                  </span>
                  <span className="flex items-center gap-1 text-cyan-400 text-sm">
                    <Twitter size={16} /> {nartuStudio.social.twitter}
                  </span>
                </div>
              </div>
              <div className="w-full md:w-48 h-32 glass-card rounded-xl flex items-center justify-center">
                <span className="text-gray-500 text-sm">NCU Logo</span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Commission Types */}
        <section className="container mx-auto px-4 py-8">
          <motion.h2
            className="text-2xl sm:text-3xl font-azonix font-bold text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Commission <span className="gradient-text">Types</span>
          </motion.h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {commissionTypes.map((type, index) => (
              <motion.div
                key={type.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card rounded-xl p-5 glow-border hover:bg-[rgba(167,139,250,0.1)] transition-all"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                  background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%)'
                }}>
                  <type.icon className="text-white" size={24} />
                </div>
                <h3 className="font-azonix font-bold text-white mb-2">{type.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{type.description}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Price:</span>
                    <span className="text-[#a78bfa] font-medium">{type.priceRange}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Time:</span>
                    <span className="text-pink-400 font-medium">{type.timeframe}</span>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1 mb-4">
                  {type.examples.slice(0, 2).map((ex, i) => (
                    <span key={i} className="px-2 py-0.5 glass-card rounded text-xs text-gray-400">
                      {ex}
                    </span>
                  ))}
                </div>
                
                {/* Wireframe Button */}
                <button 
                  onClick={() => setContactForm({ ...contactForm, type: type.id })}
                  className="w-full py-2 glass-card text-[#a78bfa] rounded-lg text-sm font-semibold hover:bg-[rgba(167,139,250,0.2)] transition-colors"
                >
                  Select
                </button>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Portfolio Showcase */}
        <section className="container mx-auto px-4 py-8">
          <motion.h2
            className="text-2xl sm:text-3xl font-azonix font-bold text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Featured <span className="text-pink-400">Portfolio</span>
          </motion.h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {portfolio.map((art, index) => (
              <motion.div
                key={art.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="relative group rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setSelectedArtwork(art)}
              >
                <div 
                  className="aspect-[4/3] bg-cover bg-center group-hover:scale-110 transition-transform duration-500"
                  style={{ backgroundImage: `url(${art.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h4 className="font-azonix font-bold text-white">{art.title}</h4>
                    <p className="text-gray-400 text-sm">{art.type} • {art.artist}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="container mx-auto px-4 py-12 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-xl mx-auto glass-card rounded-2xl p-6 sm:p-8 glow-border"
          >
            <h2 className="text-2xl font-azonix font-bold text-center mb-2">Request a Commission</h2>
            <p className="text-gray-400 text-center mb-6">Fill out the form below and we'll connect you with our artists.</p>
            
            {isSubmitted ? (
              <div className="p-6 glass-card border border-green-500/30 rounded-xl flex items-center gap-3">
                <CheckCircle className="text-green-400" size={24} />
                <p className="text-green-300">Request submitted! We'll contact you within 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Your Name</label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-4 py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-lg text-white focus:border-[#a78bfa] outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-4 py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-lg text-white focus:border-[#a78bfa] outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Commission Type</label>
                  <select
                    value={contactForm.type}
                    onChange={(e) => setContactForm({ ...contactForm, type: e.target.value })}
                    className="w-full px-4 py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-lg text-white focus:border-[#a78bfa] outline-none"
                  >
                    {commissionTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Description</label>
                  <textarea
                    value={contactForm.description}
                    onChange={(e) => setContactForm({ ...contactForm, description: e.target.value })}
                    rows="4"
                    placeholder="Describe your commission idea..."
                    className="w-full px-4 py-3 glass-card border border-[rgba(167,139,250,0.3)] rounded-lg text-white focus:border-[#a78bfa] outline-none resize-none"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3 glass-card text-[#a78bfa] rounded-lg font-semibold hover:bg-[rgba(167,139,250,0.2)] transition-colors"
                >
                  <Send size={18} /> Submit Request
                </button>
              </form>
            )}
          </motion.div>
        </section>

        {/* Artwork Detail Modal */}
        <AnimatePresence>
          {selectedArtwork && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedArtwork(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="glass-card rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden glow-border"
                onClick={(e) => e.stopPropagation()}
              >
                <div 
                  className="h-80 bg-cover bg-center"
                  style={{ backgroundImage: `url(${selectedArtwork.image})` }}
                />
                <div className="p-6 relative">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-2">{selectedArtwork.title}</h2>
                  <p className="text-gray-400 mb-4">{selectedArtwork.type} • {selectedArtwork.artist}</p>
                  <button
                    onClick={() => setSelectedArtwork(null)}
                    className="absolute top-4 right-4 p-2 glass-card text-white rounded-full hover:bg-[rgba(167,139,250,0.2)]"
                  >
                    <X size={20} />
                  </button>
                  <button
                    onClick={() => setContactForm({ ...contactForm, type: 'character' })}
                    className="w-full py-3 glass-card text-[#a78bfa] rounded-lg font-semibold hover:bg-[rgba(167,139,250,0.2)] transition-colors"
                  >
                    Request Similar
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
